import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { listAdmins, addAdmin, removeAdmin, type AdminRow } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { formatTimestamp } from "@/lib/utils";

export function AdminsRoute() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);

  const [discordId, setDiscordId] = useState("");
  const [note, setNote] = useState("");

  const query = useQuery({
    queryKey: ["admins"],
    queryFn: listAdmins,
  });

  const add = useMutation({
    mutationFn: () => addAdmin(discordId.trim(), note.trim() || undefined),
    onSuccess: () => {
      setDiscordId("");
      setNote("");
      qc.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeAdmin(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
  });

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (me?.role !== "internal") return; // hard guard — server also returns 403
    if (!/^\d+$/.test(discordId.trim())) return;
    add.mutate();
  }

  function handleRemove(row: AdminRow) {
    if (!confirm(`Remove ${row.global_name ?? row.username ?? row.discord_id} from the admin whitelist?`)) return;
    remove.mutate(row.discord_id);
  }

  const isBoot = me?.role === "internal";
  const envAdmins = query.data?.envAdmins ?? [];
  const dbAdmins = query.data?.admins ?? [];
  // The server already filters env admins out of `admins` for non-bootstrap
  // viewers. For bootstrap viewers we still split them into two groups locally.
  const envSet = new Set(envAdmins);
  const visibleDbAdmins = isBoot ? dbAdmins.filter((a) => !envSet.has(a.discord_id)) : dbAdmins;
  const seenEnvAdmins = isBoot ? dbAdmins.filter((a) => envSet.has(a.discord_id)) : [];

  return (
    <AppShell title="Admins" subtitle="Discord whitelist for who can sign in">
      <div className="space-y-6 max-w-4xl">
        {!isBoot && (
          <Alert>
            <AlertDescription className="text-xs">
              View-only mode. Only bootstrap (env) admins can add or remove
              entries from the whitelist.
            </AlertDescription>
          </Alert>
        )}

        {/* Add form — bootstrap admins only */}
        {isBoot && (
          <form onSubmit={handleAdd} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <h3 className="text-sm font-medium">Add admin</h3>
            <div className="flex gap-2">
              <Input
                value={discordId}
                onChange={(e) => setDiscordId(e.target.value)}
                placeholder="Discord user ID (e.g. 123456789012345678)"
                className="font-mono w-72"
              />
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional, e.g. role)"
                className="flex-1"
              />
              <Button type="submit" disabled={!discordId.trim() || add.isPending}>
                {add.isPending ? "Adding…" : "Add"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              User must enable Developer Mode in Discord, then right-click their name → Copy User ID.
            </p>
            {add.error && (
              <Alert variant="destructive">
                <AlertDescription>{String(add.error)}</AlertDescription>
              </Alert>
            )}
          </form>
        )}

        {/* Bootstrap (env) admins — bootstrap admins only */}
        {isBoot && envAdmins.length > 0 && (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-4 py-2 border-b border-border/60 flex items-center gap-2">
              <h3 className="text-sm font-medium">Bootstrap admins</h3>
              <Badge variant="outline" className="text-[10px]">env var</Badge>
              <span className="text-xs text-muted-foreground">— always allowed; remove from <code>ADMIN_DISCORD_IDS</code> to revoke</span>
            </div>
            <Table rows={seenEnvAdmins} envAdmins={envAdmins} myDiscordId={me?.discordId ?? null} envOnly />
          </div>
        )}

        {/* DB-managed admins */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="px-4 py-2 border-b border-border/60 flex items-center justify-between">
            <h3 className="text-sm font-medium">Whitelist</h3>
            <span className="text-xs text-muted-foreground">{visibleDbAdmins.length} entr{visibleDbAdmins.length === 1 ? "y" : "ies"}</span>
          </div>
          {query.isLoading ? (
            <div className="p-4"><Skeleton className="h-24 w-full" /></div>
          ) : visibleDbAdmins.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">No additional admins yet.</div>
          ) : (
            <Table
              rows={visibleDbAdmins}
              envAdmins={envAdmins}
              myDiscordId={me?.discordId ?? null}
              onRemove={isBoot ? handleRemove : undefined}
            />
          )}
          {remove.error && (
            <div className="p-3">
              <Alert variant="destructive">
                <AlertDescription>{String(remove.error)}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

interface TableProps {
  rows: AdminRow[];
  envAdmins: string[];
  myDiscordId: string | null;
  envOnly?: boolean;
  onRemove?: (row: AdminRow) => void;
}

function Table({ rows, envAdmins, myDiscordId, envOnly, onRemove }: TableProps) {
  const envSet = new Set(envAdmins);
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/40 text-xs text-muted-foreground">
        <tr>
          <th className="px-3 py-2 text-left">Discord ID</th>
          <th className="px-3 py-2 text-left">Name</th>
          <th className="px-3 py-2 text-left">Added</th>
          <th className="px-3 py-2 text-left">Added by</th>
          <th className="px-3 py-2 text-left">Note</th>
          <th className="px-3 py-2 w-10"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isMe = r.discord_id === myDiscordId;
          const isEnv = envSet.has(r.discord_id);
          return (
            <tr key={r.discord_id} className="border-t border-border/50">
              <td className="px-3 py-2 font-mono text-xs">
                {r.discord_id}
                {isMe && <Badge variant="secondary" className="ml-2 text-[10px]">you</Badge>}
              </td>
              <td className="px-3 py-2 text-xs">
                {r.global_name ?? r.username ?? <span className="text-muted-foreground italic">not signed in yet</span>}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatTimestamp(r.added_at)}</td>
              <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{r.added_by ?? "—"}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-xs" title={r.note ?? ""}>
                {r.note ?? "—"}
              </td>
              <td className="px-3 py-2 text-right">
                {onRemove && !envOnly && !isEnv && !isMe && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onRemove(r)}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
